import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import RotateDevice from "../../components/RotateDevice/RotateDevice";
import Lf23Loader from "../../components/Lf23Loader";
import ModaleStoricoTratta from "./ModaleStoricoTratta";
import { FiltroMulti, valoriDi, opzioniDa, BadgeValidita, FiltroValidita, CampiValidita, AvvisoSenzaZona } from "./comuni";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const EMPTY_FORM = {
    id: null, codice: "", strada: "", tratto: "", note: "", areaCritica: false, validoDal: "", validoAl: "",
    fasciaId: "", quartiereId: "", passAnno: "", lunghezzaMetri: "", nrCestini: "",
};

// Nel form i campi numerici sono stringhe ("" = vuoto); al backend vanno numero o null.
const numeroONull = (v) => (v === "" || v == null ? null : Number(v));
const stringaDa = (v) => (v == null ? "" : String(v));

// Opzione "vuoto" dei filtri Quartiere/Percorso: uguale a FILTRO_NESSUNO nel controller.
const FILTRO_NESSUNO = "__nessuno__";

const TabTratti = ({ zona }) => {
    const [dati, setDati] = useState([]);
    const [stats, setStats] = useState({ totali: 0, areeCritiche: 0 });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const [showFilters, setShowFilters] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [query, setQuery] = useState({ search: "", strada: "", areaCritica: "", fascia: "", quartiere: "", passAnno: "", percorso: "", sortKey: "", sortDir: "asc" });
    // Validità (switch percorsi): di default le correnti, cioè attive + in arrivo.
    const [fValidita, setFValidita] = useState("correnti");
    // valori del pannello filtri (applicati su "Applica")
    const [fStrada, setFStrada] = useState("");
    const [fArea, setFArea] = useState("");
    const [fFascia, setFFascia] = useState([]);
    const [fQuartiere, setFQuartiere] = useState([]);
    const [fPassAnno, setFPassAnno] = useState([]);
    const [fPercorso, setFPercorso] = useState([]);
    // opzioni dei select (Fascia/Pass-Anno), fornite dal backend
    const [filterOpts, setFilterOpts] = useState({ fasce: [], quartieri: [], passAnni: [], percorsi: [] });
    // opzioni delle tendine del modale crea/modifica (enum con id), fornite dal backend
    const [formOpts, setFormOpts] = useState({ fasce: [], quartieri: [], passAnni: [] });

    // modale crea/modifica
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");

    // modale elimina
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteError, setDeleteError] = useState("");
    // Modale "Dettaglio": storico del tratto nei percorsi 122 (task 483). Null = chiuso.
    const [storicoTarget, setStoricoTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // vista dati: tabella (default da 1025px in su) oppure card (default da 1024px in giù)
    const [viewMode, setViewMode] = useState(() => (window.matchMedia("(max-width: 1024px)").matches ? "cards" : "table"));
    // tabella a tutto schermo (fixed sull'intero viewport, con invito a ruotare su mobile)
    const [isFullscreen, setIsFullscreen] = useState(false);

    const fetchData = useCallback(async (opts = {}) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/tratti`, {
                headers: authHeaders(),
                params: {
                    zona,
                    page,
                    limit,
                    search: query.search || undefined,
                    strada: query.strada || undefined,
                    areaCritica: query.areaCritica || undefined,
                    // liste come CSV: il backend le riporta ad array (listaParam)
                    fascia: query.fascia || undefined,
                    quartiere: query.quartiere || undefined,
                    passAnno: query.passAnno || undefined,
                    percorso: query.percorso || undefined,
                    validita: fValidita,
                    sortKey: query.sortKey || undefined,
                    sortDir: query.sortDir || undefined,
                    refresh: opts.refresh ? 1 : undefined,
                },
            });
            setDati(res.data?.data ?? []);
            const pag = res.data?.pagination ?? {};
            setTotalPages(pag.totalPages ?? 1);
            setTotalItems(pag.totalItems ?? 0);
            setStats(res.data?.stats ?? { totali: 0, areeCritiche: 0 });
            setFilterOpts(res.data?.filtri ?? { fasce: [], quartieri: [], passAnni: [], percorsi: [] });
            setFormOpts(res.data?.opzioni ?? { fasce: [], quartieri: [], passAnni: [] });
        } catch (err) {
            console.error("Errore nel fetch tratti:", err);
            setDati([]);
        } finally {
            setLoading(false);
        }
    }, [zona, page, limit, query, fValidita]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // debounce della ricerca libera
    useEffect(() => {
        const t = setTimeout(() => {
            setQuery((q) => (q.search === searchInput ? q : { ...q, search: searchInput }));
            setPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    // ESC esce dalla tabella a tutto schermo
    useEffect(() => {
        if (!isFullscreen) return;
        const onEsc = (e) => { if (e.key === "Escape") setIsFullscreen(false); };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [isFullscreen]);

    // Cambio vista automatico al superamento della soglia 1024px, SENZA ricaricare:
    // <=1024 -> card, >=1025 -> tabella (riapplica il default della fascia).
    // "change" scatta solo attraversando la soglia, non a ogni resize.
    useEffect(() => {
        const mql = window.matchMedia("(max-width: 1024px)");
        const onChange = (e) => setViewMode(e.matches ? "cards" : "table");
        mql.addEventListener("change", onChange);
        return () => mql.removeEventListener("change", onChange);
    }, []);

    const applyFilters = () => {
        setQuery((q) => ({ ...q, strada: fStrada, areaCritica: fArea,
            fascia: valoriDi(fFascia).join(","), quartiere: valoriDi(fQuartiere).join(","),
            passAnno: valoriDi(fPassAnno).join(","), percorso: valoriDi(fPercorso).join(",") }));
        setPage(1);
    };
    const resetFilters = () => {
        setFStrada("");
        setFArea("");
        setFFascia([]);
        setFQuartiere([]);
        setFPassAnno([]);
        setFPercorso([]);
        setSearchInput("");
        setQuery({ search: "", strada: "", areaCritica: "", fascia: "", quartiere: "", passAnno: "", percorso: "", sortKey: "", sortDir: "asc" });
        setPage(1);
    };

    const toggleSort = (key) => {
        setQuery((q) => ({ ...q, sortKey: key, sortDir: q.sortKey === key && q.sortDir === "asc" ? "desc" : "asc" }));
        setPage(1);
    };
    const sortArrow = (key) => (query.sortKey === key ? (query.sortDir === "asc" ? " ▲" : " ▼") : "");

    const openNew = () => { setForm(EMPTY_FORM); setFormError(""); setModalOpen(true); };
    const openEdit = (t) => {
        setForm({
            id: t.id, codice: t.codice, strada: t.strada, tratto: t.tratto, note: t.note, areaCritica: t.areaCritica,
            validoDal: t.validoDal || "", validoAl: t.validoAl || "",
            fasciaId: stringaDa(t.fasciaId), quartiereId: stringaDa(t.quartiereId), passAnno: stringaDa(t.passAnno),
            lunghezzaMetri: stringaDa(t.lunghezzaMetri), nrCestini: stringaDa(t.nrCestini),
        });
        setFormError("");
        setModalOpen(true);
    };

    const saveTratto = async () => {
        if (!form.strada.trim() || !form.tratto.trim()) {
            setFormError("Strada e Tratto sono obbligatori.");
            return;
        }
        setSaving(true);
        setFormError("");
        try {
            const body = {
                strada: form.strada,
                tratto: form.tratto,
                note: form.note,
                areaCritica: form.areaCritica,
                validoDal: form.validoDal || null,
                validoAl: form.validoAl || null,
                fasciaId: numeroONull(form.fasciaId),
                quartiereId: numeroONull(form.quartiereId),
                passAnno: numeroONull(form.passAnno),
                lunghezzaMetri: numeroONull(form.lunghezzaMetri),
                nrCestini: numeroONull(form.nrCestini),
            };
            if (form.id) {
                await axios.put(`${API_URL}/gestione-percorsi/tratti/${form.id}`, { ...body, codice: form.codice }, { headers: authHeaders() });
            } else {
                // Una tratta nuova appartiene alla zona del tab aperto; in modifica la zona non cambia.
                await axios.post(`${API_URL}/gestione-percorsi/tratti`, { ...body, zona }, { headers: authHeaders() });
            }
            setModalOpen(false);
            await fetchData();
        } catch (err) {
            console.error("Errore salvataggio tratto:", err);
            setFormError(err.response?.data?.error || "Errore nel salvataggio. Riprova.");
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setDeleteError("");
        try {
            await axios.delete(`${API_URL}/gestione-percorsi/tratti/${deleteTarget.id}`, { headers: authHeaders() });
            setDeleteTarget(null);
            await fetchData();
        } catch (err) {
            console.error("Errore eliminazione tratto:", err);
            setDeleteError(err.response?.data?.error || "Errore nell'eliminazione. Riprova.");
        } finally {
            setDeleting(false);
        }
    };

    // Paginazione condivisa tra vista tabella e vista card (stesso identico markup).
    // Bottone "Dettaglio" (storico del tratto nei percorsi): stesso in tabella e in card.
    const bottoneStorico = (r) => (
        <button className="gp-dettaglio-btn" title="Dettaglio: storico del tratto nei percorsi" aria-label={`Storico di ${r.strada || r.codice}`}
            onClick={() => setStoricoTarget(r)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><polyline points="12 16 16 12 12 8" /><line x1="8" y1="12" x2="16" y2="12" />
            </svg>
        </button>
    );

    const paginationBar = (
        <div className="pagination">
            <button className="page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg> Prec
            </button>
            <span className="page-info">Pag <strong>{page}</strong> di <strong>{totalPages}</strong></span>
            <span className="page-total">({totalItems} tratti totali)</span>
            <button className="page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Succ <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
            </button>
            <div className="gp-perpage">
                <span>Per pagina:</span>
                {[10, 20, 50, 100].map((n) => (
                    <button key={n} className={`gp-perpage-btn ${limit === n ? "active" : ""}`} onClick={() => { setLimit(n); setPage(1); }}>{n}</button>
                ))}
            </div>
        </div>
    );

    return (
        <>
            {/* Loader a tutto schermo (parte subito al salvataggio/eliminazione) */}
            {(saving || deleting) && (
                <div className="gp-save-overlay">
                    <div className="gp-save-card">
                        <Lf23Loader testo={deleting ? "Eliminazione in corso..." : "Salvataggio su Interacta..."} />
                    </div>
                </div>
            )}

            <div className="gp-toolbar-row">
            {/* Toggle filtri (subito dopo l'header) */}
            <button className={`toggle-filters-btn ${showFilters ? "active" : ""}`} onClick={() => setShowFilters((v) => !v)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
                {showFilters ? "Nascondi Filtri" : "Mostra Filtri"}
                <svg className="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
            </button>

            {showFilters && (
                <div className="filters-panel">
                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>Strada</label>
                            <input type="text" placeholder="Nome strada..." value={fStrada} onChange={(e) => setFStrada(e.target.value)} />
                        </div>
                        <div className="filter-group">
                            <label>Area critica</label>
                            <select value={fArea} onChange={(e) => setFArea(e.target.value)}>
                                <option value="">Tutte</option>
                                <option value="si">Sì</option>
                                <option value="no">No</option>
                            </select>
                        </div>
                        <div className="filter-group">
                            <label>Fascia</label>
                            <FiltroMulti placeholder="Tutte" options={opzioniDa(filterOpts.fasce)}
                                value={fFascia} onChange={setFFascia} />
                        </div>
                        <div className="filter-group">
                            <label>Quartiere</label>
                            <FiltroMulti placeholder="Tutti"
                                options={[...opzioniDa(filterOpts.quartieri), { value: FILTRO_NESSUNO, label: "(senza quartiere)" }]}
                                value={fQuartiere} onChange={setFQuartiere} />
                        </div>
                        <div className="filter-group">
                            <label>Pass/Anno</label>
                            <FiltroMulti placeholder="Tutti" options={opzioniDa(filterOpts.passAnni)}
                                value={fPassAnno} onChange={setFPassAnno} />
                        </div>
                        <div className="filter-group">
                            <label>Percorso</label>
                            <FiltroMulti placeholder="Tutti"
                                options={[...filterOpts.percorsi.map((p) => ({ value: String(p.id), label: p.nome })), { value: FILTRO_NESSUNO, label: "(nessun percorso)" }]}
                                value={fPercorso} onChange={setFPercorso} />
                        </div>
                    </div>
                    <div className="filter-actions">
                        <button className="btn btn-ghost" onClick={resetFilters}>Reset</button>
                        <button className="btn btn-primary" onClick={applyFilters}>Applica</button>
                    </div>
                </div>
            )}

            <div className="toolbar">
                <div className="search-box">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                    <input type="text" placeholder="Cerca per codice, strada o tratto..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
                </div>
                <span className="gp-tb-break" aria-hidden="true" />
                <div className="spacer" />
                <button className="btn btn-ghost gp-new-btn" onClick={() => fetchData({ refresh: true })} title="Ricarica da Interacta (ignora la cache)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
                    Aggiorna
                </button>
                <span className="results-badge">{dati.length} di {totalItems}</span>
                <button className="btn btn-primary gp-new-btn" onClick={openNew}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Nuovo Tratto
                </button>
            </div>
            </div>

            {loading ? (
                <div className="gp-loading">
                    <Lf23Loader testo="Caricamento tratti in corso..." />
                </div>
            ) : (
            <>
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
                    </div>
                    <div><p className="stat-label">Tratti totali</p><p className="stat-value">{stats.totali}</p></div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon amber">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                    </div>
                    <div><p className="stat-label">Aree critiche</p><p className="stat-value">{stats.areeCritiche}</p></div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon slate">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                    </div>
                    <div><p className="stat-label">Risultati filtrati</p><p className="stat-value">{totalItems}</p></div>
                </div>
            </div>

            <AvvisoSenzaZona n={stats.senzaZona} cosa="tratte" />

            <div className="gp-view-bar">
                <FiltroValidita value={fValidita} onChange={(v) => { setFValidita(v); setPage(1); }} conteggi={stats.validita} />
                <div className="view-toggle-compact">
                    <button className={`view-btn-compact ${viewMode === "table" ? "active" : ""}`} onClick={() => setViewMode("table")}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9h18M3 15h18M9 3v18" /></svg>
                        Tabella
                    </button>
                    <button className={`view-btn-compact ${viewMode === "cards" ? "active" : ""}`} onClick={() => setViewMode("cards")}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
                        Card
                    </button>
                </div>
            </div>

            {viewMode === "table" ? (
            <div className={`table-container${isFullscreen ? " gp-fullscreen-table" : ""}`}>
                <RotateDevice active={isFullscreen} maxWidth={1024} />
                <div className="gp-table-topbar">
                    <button className="gp-fullscreen-btn" onClick={() => setIsFullscreen((v) => !v)} title={isFullscreen ? "Esci da tutto schermo" : "Tutto schermo"}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {isFullscreen ? (
                                <>
                                    <polyline points="4 14 10 14 10 20" />
                                    <polyline points="20 10 14 10 14 4" />
                                    <line x1="14" y1="10" x2="21" y2="3" />
                                    <line x1="3" y1="21" x2="10" y2="14" />
                                </>
                            ) : (
                                <>
                                    <polyline points="15 3 21 3 21 9" />
                                    <polyline points="9 21 3 21 3 15" />
                                    <line x1="21" y1="3" x2="14" y2="10" />
                                    <line x1="3" y1="21" x2="10" y2="14" />
                                </>
                            )}
                        </svg>
                    </button>
                </div>
                <div className="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => toggleSort("codice")} style={{ cursor: "pointer" }}>Codice{sortArrow("codice")}</th>
                                <th onClick={() => toggleSort("strada")} style={{ cursor: "pointer" }}>Strada{sortArrow("strada")}</th>
                                <th onClick={() => toggleSort("tratto")} style={{ cursor: "pointer" }}>Tratto{sortArrow("tratto")}</th>
                                <th onClick={() => toggleSort("areaCritica")} style={{ cursor: "pointer" }}>Area critica{sortArrow("areaCritica")}</th>
                                <th onClick={() => toggleSort("fascia")} style={{ cursor: "pointer" }}>Fascia{sortArrow("fascia")}</th>
                                <th onClick={() => toggleSort("quartiere")} style={{ cursor: "pointer" }}>Quartiere{sortArrow("quartiere")}</th>
                                <th onClick={() => toggleSort("passAnno")} style={{ cursor: "pointer" }}>Pass/Anno{sortArrow("passAnno")}</th>
                                <th onClick={() => toggleSort("lunghezzaMetri")} style={{ cursor: "pointer" }}>Lunghezza (m){sortArrow("lunghezzaMetri")}</th>
                                <th onClick={() => toggleSort("nrCestini")} style={{ cursor: "pointer" }}>Nr Cestini{sortArrow("nrCestini")}</th>
                                <th>Note</th>
                                <th>Percorsi associati</th>
                                <th>Validità</th>
                                <th className="col-actions">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!loading && dati.map((r) => (
                                <tr key={r.id}>
                                    <td><span className="codice">{r.codice || "—"}</span></td>
                                    <td><span className="strong">{r.strada || "—"}</span></td>
                                    <td><span className="sub">{r.tratto || "—"}</span></td>
                                    <td>{r.areaCritica ? <span className="badge badge-warning">Sì</span> : <span className="badge badge-neutral">No</span>}</td>
                                    <td>{r.fascia ? <span className="badge badge-info">{r.fascia}</span> : "—"}</td>
                                    <td>{r.quartiere || "—"}</td>
                                    <td>{r.passAnno != null ? r.passAnno : "—"}</td>
                                    <td>{r.lunghezzaMetri != null ? r.lunghezzaMetri : "—"}</td>
                                    <td>{r.nrCestini != null ? r.nrCestini : "—"}</td>
                                    <td><span className="note-cell">{r.note || "—"}</span></td>
                                    <td>
                                        {r.percorsi && r.percorsi.length > 0 ? (
                                            <div className="gp-percorsi-cell">
                                                {r.percorsi.map((p) => (
                                                    <span key={p.id} className="gp-percorso-pill" title={p.itinerario || ""}>{p.nome || p.itinerario || "—"}</span>
                                                ))}
                                            </div>
                                        ) : <span className="muted">—</span>}
                                    </td>
                                    <td><BadgeValidita riga={r} /></td>
                                    <td className="col-actions">
                                        {bottoneStorico(r)}
                                        <button className="icon-btn" title="Modifica" onClick={() => openEdit(r)}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                        </button>
                                        <button className="icon-btn danger" title="Elimina" onClick={() => { setDeleteError(""); setDeleteTarget(r); }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {dati.length === 0 && <div className="gp-empty">Nessun tratto trovato.</div>}
                </div>
                {paginationBar}
            </div>
            ) : (
            <>
                {dati.length === 0 ? (
                    <div className="gp-empty">Nessun tratto trovato.</div>
                ) : (
                    <div className="gp-cards-grid">
                        {dati.map((r) => (
                            <div className="gp-card" key={r.id}>
                                <div className="gp-card-head">
                                    <div className="gp-card-title">
                                        {r.strada || "—"}
                                        <span className="codice">{r.codice || "—"}</span>
                                    </div>
                                    {r.areaCritica ? <span className="badge badge-warning">Area critica</span> : <span className="badge badge-neutral">Standard</span>}
                                </div>
                                <div className="gp-card-body">
                                    <div className="gp-card-row"><span className="gp-card-label">Tratto</span><span className="gp-card-value">{r.tratto || "—"}</span></div>
                                    <div className="gp-card-row"><span className="gp-card-label">Fascia</span><span className="gp-card-value">{r.fascia ? <span className="badge badge-info">{r.fascia}</span> : "—"}</span></div>
                                    <div className="gp-card-row"><span className="gp-card-label">Quartiere</span><span className="gp-card-value">{r.quartiere || "—"}</span></div>
                                    <div className="gp-card-row"><span className="gp-card-label">Pass/Anno</span><span className="gp-card-value">{r.passAnno != null ? r.passAnno : "—"}</span></div>
                                    <div className="gp-card-row"><span className="gp-card-label">Lunghezza (m)</span><span className="gp-card-value">{r.lunghezzaMetri != null ? r.lunghezzaMetri : "—"}</span></div>
                                    <div className="gp-card-row"><span className="gp-card-label">Nr Cestini</span><span className="gp-card-value">{r.nrCestini != null ? r.nrCestini : "—"}</span></div>
                                    <div className="gp-card-row"><span className="gp-card-label">Note</span><span className="gp-card-value note-cell">{r.note || "—"}</span></div>
                                    <div className="gp-card-row"><span className="gp-card-label">Percorsi associati</span><span className="gp-card-value">{r.percorsi && r.percorsi.length > 0 ? (<span className="gp-percorsi-cell">{r.percorsi.map((p) => <span key={p.id} className="gp-percorso-pill" title={p.itinerario || ""}>{p.nome || p.itinerario || "—"}</span>)}</span>) : "—"}</span></div>
                                    <div className="gp-card-row"><span className="gp-card-label">Validità</span><span className="gp-card-value"><BadgeValidita riga={r} /></span></div>
                                </div>
                                <div className="gp-card-actions">
                                    {bottoneStorico(r)}
                                        <button className="icon-btn" title="Modifica" onClick={() => openEdit(r)}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                    </button>
                                    <button className="icon-btn danger" title="Elimina" onClick={() => { setDeleteError(""); setDeleteTarget(r); }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {paginationBar}
            </>
            )}
            </>
            )}

            {/* ===== MODALE CREA/MODIFICA TRATTO ===== */}
            {modalOpen && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}>
                    <div className="modal">
                        <div className="modal-header">
                            <div className="m-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3>{form.id ? "Modifica tratto" : "Nuovo tratto"}</h3>
                                <div className="h-sub">Community 120 (Tratti Strade)</div>
                            </div>
                            <button className="modal-close" onClick={() => setModalOpen(false)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                        {formError && <div className="modal-error">{formError}</div>}
                        <div className="modal-body">
                            <div className="form-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                                <div className="form-group full">
                                    <label>Codice</label>
                                    <input type="text" value={form.id ? form.codice : "(assegnato automaticamente)"} disabled />
                                </div>
                                <div className="form-group full">
                                    <label>Strada</label>
                                    <input type="text" value={form.strada} onChange={(e) => setForm((f) => ({ ...f, strada: e.target.value }))} placeholder="Es. VIA DELL'ARCOVEGGIO" />
                                </div>
                                <div className="form-group full">
                                    <label>Tratto</label>
                                    <input type="text" value={form.tratto} onChange={(e) => setForm((f) => ({ ...f, tratto: e.target.value }))} placeholder="Es. da Fioravanti a Giuriolo" />
                                </div>
                                <div className="form-group full">
                                    <label>Note</label>
                                    <textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Es. DX-SX, compreso parcheggi" />
                                </div>
                                <div className="form-group full">
                                    <label>Quartiere</label>
                                    <select value={form.quartiereId} onChange={(e) => setForm((f) => ({ ...f, quartiereId: e.target.value }))}>
                                        <option value="">—</option>
                                        {formOpts.quartieri.map((o) => <option key={o.id} value={String(o.id)}>{o.label}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Fascia</label>
                                    <select value={form.fasciaId} onChange={(e) => setForm((f) => ({ ...f, fasciaId: e.target.value }))}>
                                        <option value="">—</option>
                                        {formOpts.fasce.map((o) => <option key={o.id} value={String(o.id)}>{o.label}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Pass/Anno</label>
                                    <select value={form.passAnno} onChange={(e) => setForm((f) => ({ ...f, passAnno: e.target.value }))}>
                                        <option value="">—</option>
                                        {/* il valore attuale resta selezionabile anche se non è fra quelli in uso */}
                                        {[...new Set([...formOpts.passAnni.map(String), form.passAnno].filter(Boolean))]
                                            .sort((a, b) => Number(a) - Number(b))
                                            .map((v) => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Lunghezza (m)</label>
                                    <input type="number" min="0" step="1" value={form.lunghezzaMetri} onChange={(e) => setForm((f) => ({ ...f, lunghezzaMetri: e.target.value }))} placeholder="Es. 350" />
                                </div>
                                <div className="form-group">
                                    <label>Nr Cestini</label>
                                    <input type="number" min="0" step="1" value={form.nrCestini} onChange={(e) => setForm((f) => ({ ...f, nrCestini: e.target.value }))} placeholder="Es. 4" />
                                </div>
                                <div className="form-group full">
                                    <div className="switch-row">
                                        <span>Area critica</span>
                                        <label className="switch">
                                            <input type="checkbox" checked={form.areaCritica} onChange={(e) => setForm((f) => ({ ...f, areaCritica: e.target.checked }))} />
                                            <span className="slider" />
                                        </label>
                                    </div>
                                </div>
                                <CampiValidita form={form} setForm={setForm} />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <div className="footer-hint" />
                            <div className="footer-actions">
                                <button className="btn btn-ghost" onClick={() => setModalOpen(false)} disabled={saving}>Annulla</button>
                                <button className="btn btn-primary" onClick={saveTratto} disabled={saving}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                                    {saving ? "Salvataggio..." : "Salva"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== MODALE ELIMINA ===== */}
            {/* ===== STORICO DEL TRATTO (Dettaglio) ===== */}
            {storicoTarget && (
                <ModaleStoricoTratta
                    trattoId={storicoTarget.id}
                    intestazione={{ via: storicoTarget.strada, tratto: storicoTarget.tratto, codice: storicoTarget.codice, quartiere: storicoTarget.quartiere }}
                    onClose={() => setStoricoTarget(null)}
                />
            )}

            {deleteTarget && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setDeleteTarget(null); }}>
                    <div className="modal danger" style={{ maxWidth: 460 }}>
                        <div className="modal-header">
                            <div className="m-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                            </div>
                            <h3>Confermi l'eliminazione?</h3>
                            <button className="modal-close" onClick={() => setDeleteTarget(null)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="modal-text">
                                Stai per eliminare il tratto <strong>{deleteTarget.codice} — {deleteTarget.strada}</strong> dall'anagrafica (community 120).
                                I percorsi che lo referenziano andranno aggiornati.
                            </p>
                            {deleteError && <div className="modal-error">{deleteError}</div>}
                        </div>
                        <div className="modal-footer">
                            <div />
                            <div className="footer-actions">
                                <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>Annulla</button>
                                <button className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>{deleting ? "Eliminazione..." : "Elimina"}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TabTratti;
